import type { CSSProperties, Ref } from 'vue'

export type DraggableFabPos = { x: number; y: number }

export type UseDraggableFabOptions = {
  /** 定位边界容器（一般为浮层根节点） */
  containerEl: Ref<HTMLElement | null>
  /** 菜单块宽度（含展开项对齐宽度） */
  boxWidth?: number
  /** 触发按钮高度，用于边界与 bottom 锚定 */
  triggerSize?: number
  /** 距容器边缘的最小间距 */
  edge?: number
  /** 超过该位移才视为拖动，否则为点击 */
  dragThreshold?: number
  /** 超过容器高度该比例时，动作列表向上展开 */
  flipRatio?: number
}

/**
 * 可拖动悬浮按钮：区分点击 / 拖动，支持下半屏向上展开。
 * pos 为 null 时由外部 CSS 控制默认位置（如右上角）。
 */
export function useDraggableFab(options: UseDraggableFabOptions) {
  const boxWidth = options.boxWidth ?? 58
  const triggerSize = options.triggerSize ?? 40
  const edge = options.edge ?? 12
  const dragThreshold = options.dragThreshold ?? 8
  const flipRatio = options.flipRatio ?? 0.45

  const pos = ref<DraggableFabPos | null>(null)
  const dragging = ref(false)
  const flip = ref(false)
  const open = ref(false)

  let pointerId: number | null = null
  let dragMoved = false
  let startX = 0
  let startY = 0
  let origX = 0
  let origY = 0

  const style = computed<CSSProperties | undefined>(() => {
    if (!pos.value) return undefined
    const base: CSSProperties = {
      left: `${pos.value.x}px`,
      right: 'auto',
    }
    // 靠下用 bottom 锚定，展开时列表向上长，按钮不位移
    if (flip.value) {
      const container = options.containerEl.value
      if (container) {
        return {
          ...base,
          top: 'auto',
          bottom: `${container.clientHeight - pos.value.y - triggerSize}px`,
        }
      }
    }
    return {
      ...base,
      top: `${pos.value.y}px`,
      bottom: 'auto',
    }
  })

  const clamp = (x: number, y: number): DraggableFabPos => {
    const container = options.containerEl.value
    if (!container) return { x, y }
    const maxX = Math.max(edge, container.clientWidth - boxWidth - edge)
    const maxY = Math.max(edge, container.clientHeight - triggerSize - edge)
    return {
      x: Math.min(maxX, Math.max(edge, x)),
      y: Math.min(maxY, Math.max(edge, y)),
    }
  }

  const updateFlip = () => {
    const container = options.containerEl.value
    if (!container || !pos.value) {
      flip.value = false
      return
    }
    flip.value = pos.value.y > container.clientHeight * flipRatio
  }

  const detachListeners = () => {
    window.removeEventListener('pointermove', onPointerMove)
    window.removeEventListener('pointerup', onPointerUp)
    window.removeEventListener('pointercancel', onPointerUp)
  }

  const onPointerMove = (e: PointerEvent) => {
    if (pointerId !== null && e.pointerId !== pointerId) return
    const dx = e.clientX - startX
    const dy = e.clientY - startY
    if (!dragMoved && Math.hypot(dx, dy) < dragThreshold) return
    if (!dragMoved) {
      dragMoved = true
      dragging.value = true
      open.value = false
    }
    pos.value = clamp(origX + dx, origY + dy)
    updateFlip()
  }

  const onPointerUp = (e: PointerEvent) => {
    if (pointerId !== null && e.pointerId !== pointerId) return
    pointerId = null
    dragging.value = false
    detachListeners()

    if (dragMoved) {
      pos.value = clamp(pos.value?.x ?? origX, pos.value?.y ?? origY)
      updateFlip()
      dragMoved = false
      return
    }

    // 用 pointerup 切换展开，避免 setPointerCapture / 微位移吞掉 click
    open.value = !open.value
    if (open.value) updateFlip()
  }

  const onPointerDown = (e: PointerEvent) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return
    const container = options.containerEl.value
    if (!container) return

    const trigger = e.currentTarget as HTMLElement
    const triggerRect = trigger.getBoundingClientRect()
    const box = container.getBoundingClientRect()
    // 以触发按钮为准换算（展开/上翻时整块高度会变）
    origX = triggerRect.right - box.left - boxWidth
    origY = triggerRect.top - box.top
    startX = e.clientX
    startY = e.clientY
    dragMoved = false
    pointerId = e.pointerId

    trigger.setPointerCapture(e.pointerId)
    window.addEventListener('pointermove', onPointerMove)
    window.addEventListener('pointerup', onPointerUp)
    window.addEventListener('pointercancel', onPointerUp)
  }

  const onResize = () => {
    if (!pos.value) return
    pos.value = clamp(pos.value.x, pos.value.y)
    updateFlip()
  }

  /** 回到 CSS 默认位（如右上角），并收起菜单 */
  const reset = () => {
    pos.value = null
    dragging.value = false
    flip.value = false
    open.value = false
    dragMoved = false
    pointerId = null
  }

  watch(open, v => {
    if (v) updateFlip()
  })

  onMounted(() => {
    window.addEventListener('resize', onResize)
  })

  onBeforeUnmount(() => {
    detachListeners()
    window.removeEventListener('resize', onResize)
  })

  return {
    pos,
    dragging,
    flip,
    open,
    style,
    reset,
    onPointerDown,
  }
}
